import React, { useCallback, useMemo, useState } from 'react';
import { Copy, Check, Mic, Table2, HelpCircle } from 'lucide-react';
import classes from '../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import type { VideoScreenInsights } from '../../../services/api.service';
import {
  buildLeadershipBrief,
  buildMetricInsight,
  enrichRisksWithAssignments,
  extractTopRisks,
  formatBriefBlocksForCopy,
  formatBriefSectionsForCopy,
  formatProtocolRiskAssignment,
  getLeadershipBriefBlocks,
  getLeadershipBriefSections,
  getLeadershipBriefSummary,
  getLeadershipBriefTitle,
  getLeadershipMetrics,
  getProtocolRisks,
  isBriefHighlight,
  parseActionItems,
  resolveMetricTone,
  resolveMetricZone,
  splitBriefHighlights,
  type LeadershipBriefBlock,
  type LeadershipBriefItem,
  type LeadershipBriefSection,
  type LeadershipRisk,
  type ProtocolRisk,
} from '../utils/video-insights-leadership';
import { sectionNavLabel } from '../utils/video-insights-utils';
import VideoInsightsAccordion from './video-insights-accordion.component';

const cn = createCn('file', classes);

function BriefHighlightText({ text }: { text: string }) {
  const parts = splitBriefHighlights(text);
  return (
    <>
      {parts.map((part, i) =>
        isBriefHighlight(part) ? (
          <strong key={i} className={cn('vi-brief-highlight')}>{part}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function LeadershipBriefStructured({
  title,
  sections,
}: {
  title: string;
  sections: LeadershipBriefSection[];
}) {
  return (
    <div className={cn('vi-brief-structured')}>
      <h3 className={cn('vi-brief-structured-title')}>{title}</h3>
      <ul className={cn('vi-brief-sections')}>
        {sections.map(section => {
          const tone = section.tone || 'neutral';
          return (
            <li
              key={section.id || section.label}
              className={cn('vi-brief-section', { [tone]: true })}
            >
              <span className={cn('vi-brief-section-label')}>{section.label}</span>
              <p className={cn('vi-brief-section-text')}>
                <BriefHighlightText text={section.text} />
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BriefItemCard({ item }: { item: LeadershipBriefItem }) {
  const tone = item.tone || 'neutral';
  return (
    <article className={cn('vi-brief-item', { [tone]: true })}>
      {item.label && (
        <h4 className={cn('vi-brief-item-label')}>{item.label}</h4>
      )}
      <p className={cn('vi-brief-item-text')}>
        <BriefHighlightText text={item.text} />
      </p>
      {item.bullets && item.bullets.length > 0 && (
        <ul className={cn('vi-brief-item-bullets')}>
          {item.bullets.map((bullet, i) => (
            <li key={i}>
              <BriefHighlightText text={bullet} />
            </li>
          ))}
        </ul>
      )}
      {item.action && (
        <p className={cn('vi-brief-item-action')}>
          <span className={cn('vi-brief-item-action-label')}>Действие:</span>{' '}
          <BriefHighlightText text={item.action} />
        </p>
      )}
    </article>
  );
}

function LeadershipBriefBlocks({
  title,
  blocks,
  summary,
}: {
  title: string;
  blocks: LeadershipBriefBlock[];
  summary?: string;
}) {
  return (
    <div className={cn('vi-brief-blocks')}>
      <h3 className={cn('vi-brief-structured-title')}>{title}</h3>
      <div className={cn('vi-brief-block-list')}>
        {blocks.map(block => (
          <VideoInsightsAccordion
            key={block.id || block.title}
            badge={block.number}
            title={block.title}
            subtitle={block.subtitle}
            defaultOpen={block.number === 1}
            variant="brief"
          >
            <div className={cn('vi-brief-block-items')}>
              {block.items.map(item => (
                <BriefItemCard key={item.id || item.label || item.text.slice(0, 40)} item={item} />
              ))}
            </div>
          </VideoInsightsAccordion>
        ))}
      </div>
      {summary && (
        <aside className={cn('vi-brief-summary')}>
          <span className={cn('vi-brief-summary-label')}>Резюме для СД / Руководства</span>
          <p className={cn('vi-brief-summary-text')}>
            <BriefHighlightText text={summary} />
          </p>
        </aside>
      )}
    </div>
  );
}

type Props = {
  insights: VideoScreenInsights;
  actionItems?: string[];
  onJumpToSection?: (sectionId: string) => void;
};

function SourceAttribution({ risk }: { risk: LeadershipRisk }) {
  const { attribution } = risk;
  const Icon =
    attribution.kind === 'speaker' ? Mic : attribution.kind === 'unknown' ? HelpCircle : Table2;

  return (
    <div
      className={cn('vi-risk-source', {
        speaker: attribution.kind === 'speaker',
        unknown: attribution.kind === 'unknown',
        table: attribution.kind === 'recording',
      })}
    >
      <Icon size={12} aria-hidden />
      <span>{attribution.label}</span>
    </div>
  );
}

function ExistingRiskCard({
  risk,
  rank,
  onJump,
}: {
  risk: LeadershipRisk;
  rank: number;
  onJump?: (sectionId: string) => void;
}) {
  const sectionShort = sectionNavLabel(risk.sectionTitle);
  const assignment = risk.pendingAssignment || '';

  return (
    <article className={cn('vi-risk-card')}>
      <button
        type="button"
        className={cn('vi-risk-card-top')}
        onClick={() => onJump?.(risk.sectionId)}
      >
        <span className={cn('vi-risk-rank')} aria-hidden>
          {rank}
        </span>

        <div className={cn('vi-risk-card-main')}>
          <SourceAttribution risk={risk} />
          <p className={cn('vi-risk-headline')}>{risk.headline}</p>
          <div className={cn('vi-risk-context')}>
            <span className={cn('vi-risk-section-pill')}>{sectionShort}</span>
            <span className={cn('vi-risk-context-sep')} aria-hidden>
              ·
            </span>
            <span className={cn('vi-risk-entity')}>{risk.entityLabel}</span>
          </div>
        </div>

        <div className={cn('vi-risk-metrics')}>
          <span className={cn('vi-risk-deviation')}>{risk.deviationDisplay}</span>
          {risk.yearForecast && (
            <span className={cn('vi-risk-forecast')}>{risk.yearForecast}</span>
          )}
        </div>
      </button>

      {assignment && (
        <div className={cn('vi-risk-assignment', { pending: true })}>
          {assignment}
        </div>
      )}
    </article>
  );
}

function ProtocolRiskCard({ risk, rank }: { risk: ProtocolRisk; rank: number }) {
  const assignment = formatProtocolRiskAssignment(risk);

  return (
    <article className={cn('vi-risk-card', { protocol: true })}>
      <div className={cn('vi-risk-card-top', { static: true })}>
        <span className={cn('vi-risk-rank')} aria-hidden>
          {rank}
        </span>

        <div className={cn('vi-risk-card-main')}>
          <div className={cn('vi-risk-card-title-row')}>
            <p className={cn('vi-risk-headline')}>{risk.title}</p>
            <span className={cn('vi-risk-new-badge')}>новый</span>
            {risk.status_badge && (
              <span className={cn('vi-risk-status-badge')}>{risk.status_badge}</span>
            )}
          </div>
          {risk.description && (
            <p className={cn('vi-risk-description')}>{risk.description}</p>
          )}
          {risk.tags && risk.tags.length > 0 && (
            <div className={cn('vi-risk-tags')}>
              {risk.tags.map(tag => (
                <span key={tag} className={cn('vi-risk-section-pill')}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {risk.deviation_display && (
          <div className={cn('vi-risk-metrics')}>
            <span className={cn('vi-risk-deviation')}>{risk.deviation_display}</span>
          </div>
        )}
      </div>

      <div className={cn('vi-risk-assignment', { protocol: true })}>
        {assignment}
      </div>
    </article>
  );
}

const VideoInsightsExecutivePanel = ({ insights, actionItems = [], onJumpToSection }: Props) => {
  const [copied, setCopied] = useState(false);
  const sections = insights.sections || [];
  const parsedActions = useMemo(() => parseActionItems(actionItems), [actionItems]);
  const metrics = useMemo(() => getLeadershipMetrics(insights.leadership), [insights.leadership]);
  const briefSections = useMemo(
    () => getLeadershipBriefSections(insights.leadership),
    [insights.leadership],
  );
  const briefBlocks = useMemo(
    () => getLeadershipBriefBlocks(insights.leadership),
    [insights.leadership],
  );
  const briefSummary = useMemo(
    () => getLeadershipBriefSummary(insights.leadership),
    [insights.leadership],
  );
  const briefTitle = useMemo(
    () => getLeadershipBriefTitle(insights.leadership),
    [insights.leadership],
  );
  const protocolRisks = useMemo(() => getProtocolRisks(insights.leadership), [insights.leadership]);

  const topRisks = useMemo(
    () => enrichRisksWithAssignments(
      extractTopRisks(sections, parsedActions, insights.leadership),
      insights.leadership,
    ),
    [sections, parsedActions, insights.leadership],
  );

  const brief = useMemo(
    () => buildLeadershipBrief(sections, topRisks, insights.leadership),
    [sections, topRisks, insights.leadership],
  );

  const handleCopy = useCallback(async () => {
    const structuredBrief = briefBlocks.length
      ? formatBriefBlocksForCopy(briefBlocks, briefTitle, briefSummary)
      : formatBriefSectionsForCopy(briefSections, briefTitle);
    const lines = [
      'Для руководства',
      structuredBrief || brief,
      '',
      'Топ-риски (1–5):',
      ...topRisks.map((r, i) => {
        const assign = r.pendingAssignment || 'поручения нет';
        return `${i + 1}. ${r.headline} (${r.deviationDisplay}; ${assign})`;
      }),
      '',
      'Новые риски из протокола (6–9):',
      ...protocolRisks.map((r, i) => {
        const assign = formatProtocolRiskAssignment(r);
        return `${i + 6}. ${r.title}: ${r.description || ''} — ${assign}`;
      }),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [brief, briefBlocks, briefSections, briefSummary, briefTitle, topRisks, protocolRisks]);

  const hasBrief = Boolean(
    briefBlocks.length || briefSections.length || briefSummary || brief?.trim(),
  );

  if (!hasBrief && topRisks.length === 0 && protocolRisks.length === 0) return null;

  return (
    <section className={cn('vi-executive')} aria-label="Для руководства">
      {metrics.length > 0 && (
        <div className={cn('vi-metrics')}>
          {metrics.map(metric => {
            const tone = resolveMetricTone(metric);
            const zone = resolveMetricZone(metric);
            return (
            <div
              key={metric.label}
              className={cn('vi-metric', { [tone]: true, [`zone-${zone}`]: true })}
            >
              <span className={cn('vi-metric-label')}>{metric.label}</span>
              <span className={cn('vi-metric-value')}>{metric.value}</span>
              <span className={cn('vi-metric-insight', { [zone]: true })}>
                {buildMetricInsight(metric, zone)}
              </span>
            </div>
            );
          })}
        </div>
      )}

      <div className={cn('vi-executive-brief')}>
        <div className={cn('vi-executive-brief-head')}>
          <span className={cn('vi-executive-label')}>Для руководства</span>
          <button
            type="button"
            className={cn('vi-executive-copy')}
            onClick={() => void handleCopy()}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'скопировано' : 'копировать'}
          </button>
        </div>
        {briefBlocks.length > 0 ? (
          <LeadershipBriefBlocks
            title={briefTitle}
            blocks={briefBlocks}
            summary={briefSummary}
          />
        ) : briefSections.length > 0 ? (
          <LeadershipBriefStructured title={briefTitle} sections={briefSections} />
        ) : (
          <p className={cn('vi-executive-text')}>{brief}</p>
        )}
      </div>

      {topRisks.length > 0 && (
        <VideoInsightsAccordion
          title="Топ-риски · существующие (1–5)"
          subtitle="по отклонению от плана, все таблицы на экране"
          defaultOpen={false}
          variant="risk"
        >
          <div className={cn('vi-risks-list')}>
            {topRisks.map((risk, idx) => (
              <ExistingRiskCard
                key={risk.id}
                risk={risk}
                rank={idx + 1}
                onJump={onJumpToSection}
              />
            ))}
          </div>
        </VideoInsightsAccordion>
      )}

      {protocolRisks.length > 0 && (
        <VideoInsightsAccordion
          title="Новые риски из протокола (6–9)"
          subtitle="зафиксированы на совещании 18.06.2026"
          defaultOpen={false}
          variant="risk"
        >
          <div className={cn('vi-risks-list')}>
            {protocolRisks.map((risk, idx) => (
              <ProtocolRiskCard key={risk.id} risk={risk} rank={idx + 6} />
            ))}
          </div>
        </VideoInsightsAccordion>
      )}
    </section>
  );
};

export default VideoInsightsExecutivePanel;
